import React from "react";
import { useLocation } from "react-router-dom";
import seoPages from "../../data/seoPages.json";
import { Helmet } from "react-helmet-async";

/**
 * Site-wide SEO defaults — shared across <SEO /> usage.
 * The site URL falls back to the preview origin but is always overridable
 * via REACT_APP_SITE_URL once the production domain is configured.
 */
export const SITE_URL = "https://bowtiekreative.com";

export const SITE_NAME = "Bow Tie Kreative";
export const SITE_DEFAULT_DESC =
  "Bow Tie Kreative builds websites, sales funnels, and content systems that turn visitors into customers — led by neurodivergent strategist & 3x author Ryan Perez.";
export const DEFAULT_OG_IMAGE = "https://bowtiekreative.com/social/campaign-8a5edab28263.jpg";

const abs = (path) => {
  if (!path) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

/**
 * Per-page SEO. Pass title/description/path + optional OG image and
 * one or more JSON-LD schema objects (e.g. Product, FAQPage, Course).
 *
 * Example:
 *   <SEO
 *     title="The Vault — Lifetime Library"
 *     description="..."
 *     path="/vault"
 *     image="/vault/hero-box.jpg"
 *     schema={[productSchema, faqSchema]}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "",
  image,
  ogImage,
  ogType = "website",
  noindex = false,
  focusKeyword,
  schema,
}) {
  const location = useLocation();
  const selected = new URLSearchParams(location.search).get("product");
  const key = location.pathname === "/apply" && selected ? `/apply?product=${encodeURIComponent(selected)}` : (path || location.pathname);
  const metadata = seoPages[key] || seoPages[path];
  const pageTitle = metadata?.title || title || "Adaptive Growth Agency";
  const fullTitle = `${pageTitle.replace(/(?:\s*[·—]\s*Bow Tie Kreative)+$/, "")} · ${SITE_NAME}`;
  const desc = metadata?.description || description || SITE_DEFAULT_DESC;
  const canonicalPath = metadata ? key : (path || location.pathname);
  const url = `${SITE_URL}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}`;
  const ogImg = abs(metadata?.image || image || ogImage);
  const shouldNoindex = noindex || metadata?.noindex;
  const supplied = Array.isArray(schema) ? schema.filter(Boolean) : (schema ? [schema] : []);
  const schemaList = [orgSchema(), websiteSchema(), {
    "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#webpage`,
    url, name: pageTitle, description: desc, inLanguage: "en", isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` }, primaryImageOfPage: { "@type": "ImageObject", url: ogImg, width: 1200, height: 630 }
  }, ...supplied.filter(s => !["Organization", "WebSite"].includes(s["@type"]))];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {focusKeyword ? <meta name="keywords" content={focusKeyword} /> : null}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:image:secure_url" content={ogImg} />
      <meta property="og:image:type" content={metadata ? "image/jpeg" : undefined} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImg} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {/* Structured data (JSON-LD) */}
      {schemaList.map((s, i) => (
        <script key={s["@id"] || s["@type"] || `schema-${i}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/* ----------------------------- Schema helpers ----------------------------- */

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Bow Tie Kreative",
  alternateName: "BTK",
  url: SITE_URL,
  logo: `${SITE_URL}/btk-logo.png`,
  founder: { "@id": `${SITE_URL}/#ryan` },
  sameAs: [
    "https://x.com/bowtiekreative",
    "https://www.instagram.com/bowtiekreative",
    "https://www.facebook.com/bowtiekreative",
    "https://www.youtube.com/@bowtiekreative",
    "https://imautisticbich.com/",
    "https://socialmediauncensoredbook.com/",
    "https://frozenbylight.com/",
  ],
});

export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/#ryan`,
  name: "Ryan Perez",
  alternateName: "Ryan Perez (Bow Tie Kreative)",
  jobTitle: "Founder, Keynote Speaker, Author, Podcast Host",
  worksFor: { "@id": `${SITE_URL}/#organization` },
  url: `${SITE_URL}/the-hustle-zone`,
  image: `${SITE_URL}/the-hustle-zone/ryan-perez.jpg`,
  description:
    "Ryan Perez is a neurodivergent keynote speaker, three-time published author, podcast host, creative strategist, and the founder of Bow Tie Kreative.",
  knowsAbout: [
    "Neurodivergence",
    "Autism advocacy",
    "ADHD",
    "Entrepreneurship",
    "Content strategy",
    "Social media virality",
    "Sales funnels",
  ],
  sameAs: [
    "https://www.tiktok.com/@ryanperez",
    "https://www.instagram.com/ryanperez",
    "https://www.youtube.com/@ryanperez",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#organization` },

});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => {
    // Accept either {name, path} (relative) or {name, url} (absolute).
    let item = c.url;
    if (!item) {
      const p = c.path || "";
      item = `${SITE_URL}${p && !p.startsWith("/") ? "/" : ""}${p}`;
    }
    return {
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item,
    };
  }),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const productSchema = ({ name, description, image, price, priceCurrency = "USD", path }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  image: abs(image),
  brand: { "@type": "Brand", name: "Bow Tie Kreative" },
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}${path}`,
    priceCurrency,
    price: String(price),
    availability: "https://schema.org/InStock",
  },
});

export const courseSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name,
  description,
  provider: { "@id": `${SITE_URL}/#organization` },
  url: `${SITE_URL}${path}`,
});

export const podcastSeriesSchema = () => ({
  "@context": "https://schema.org",
  "@type": "PodcastSeries",
  name: "The Hustle Zone Podcast",
  description:
    "The Hustle Zone is a heart-led podcast hosted by Ryan Perez exploring entrepreneurship, neurodivergence, resilience, and transformation.",
  url: `${SITE_URL}/the-hustle-zone`,
  webFeed: "https://play.pod.co/go-live-and-prosper",
  author: { "@id": `${SITE_URL}/#ryan` },
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const bookSchema = ({ name, url, image, description }) => ({
  "@context": "https://schema.org",
  "@type": "Book",
  name,
  url,
  image: abs(image),
  description,
  author: { "@id": `${SITE_URL}/#ryan` },
});
